import logo from "@/assets/favicon_io/android-chrome-192x192.png";
import { config } from "@/shared/lib/config";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import {
  formatAssistInstructorBillLine,
  RECEIPT_LOGO_STYLE_48MM,
  RECEIPT_PRINT_STYLES_48MM,
  formatBillAmount,
} from "@/shared/lib/receipt-print-48mm";
import { Button, Modal, ScrollArea } from "@devhop/ui";
import { PrinterIcon } from "lucide-react";
import { useRef } from "react";

type RoomBookingReceiptPreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roomName: string;
  date: string;
  startTime: string;
  endTime: string;
  title: string | null;
  contactName: string | null;
  amount: number;
  bookingId?: string;
  assistInstructorNames?: string[];
};

export function RoomBookingReceiptPreviewDialog({
  open,
  onOpenChange,
  roomName,
  date,
  startTime,
  endTime,
  title,
  contactName,
  amount,
  bookingId,
  assistInstructorNames,
}: RoomBookingReceiptPreviewDialogProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const assistLine = formatAssistInstructorBillLine(assistInstructorNames);

  const handlePrint = async () => {
    if (!printRef.current) return;

    const logoBase64 = await new Promise<string>((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } else resolve("");
      };
      img.onerror = () => resolve("");
      img.src = logo;
    });

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const billNo = bookingId ? bookingId.slice(0, 8).toUpperCase() : "–";
    const customerName = contactName || title || "–";
    const dateTime = `${formatDate(date)} ${startTime}`;
    const address = config.brandAddress ?? "VIENTIANE CENTER, 4th floor";
    const addressLo = config.brandAddressLo ?? "ວຽງຈັນເຊັນເຕີ, ຊັ້ນ4";
    const tagline = config.billFooterTagline ?? "Keep Dancing, Keep Moving!";
    const itemLabel = title || contactName || "ຈອງຫ້ອງ";
    const logoImg =
      logoBase64 &&
      `<img src="${logoBase64}" alt="Logo" style="${RECEIPT_LOGO_STYLE_48MM}" />`;

    const content = `
    <div class="bill-header">
      ${logoImg ?? ""}
      <div class="bill-brand">${config.brandName.toUpperCase()}</div>
      <div class="bill-address">${addressLo}</div>
      <div class="bill-address">${address}</div>
    </div>
    <hr class="bill-sep" />

    <div class="bill-line">ເລກທີໃບ: ${billNo}</div>
    <div class="bill-line">ຊື່: ${customerName}</div>
    <div class="bill-line">ວັນທີ: ${dateTime}</div>
    <hr class="bill-sep" />

    <div class="bill-items">
      <div class="bill-item-line">${itemLabel}</div>
      <div class="bill-item-line">ຫ້ອງ: ${roomName}</div>
      <div class="bill-item-line">ເວລາ: ${formatTime(startTime)} - ${formatTime(endTime)}</div>
      ${assistLine ? `<div class="bill-item-line">${assistLine}</div>` : ""}
    </div>
    <hr class="bill-sep" />

    <div class="bill-total-section">
      <div class="bill-total-label">ຍອດລວມທັງໝົດ</div>
      <div class="bill-total-amount">${formatBillAmount(amount)} ກີບ</div>
    </div>
    <hr class="bill-sep" />

    <div class="bill-footer">
      <div class="bill-footer-tagline">${tagline}</div>
      <div class="bill-footer-thanks">ຂອບໃຈທີ່ໃຊ້ບໍລິການ</div>
    </div>
  `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>ບິນ - ການຈອງຫ້ອງ</title>
          <style>${RECEIPT_PRINT_STYLES_48MM}</style>
        </head>
        <body>${content}</body>
      </html>
    `);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="ພິມໃບເກັບເງິນ" size="lg">
      <ScrollArea className="max-h-[80vh] pr-2">
        <div className="space-y-6">
          <div
            ref={printRef}
            className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white p-10 shadow-lg"
          >
            <div className="mb-8 border-red-600 border-b-3 pb-6 text-center">
              <img
                src={logo}
                alt="Logo"
                className="mx-auto mb-4 size-20 rounded-lg object-contain"
              />
              <h1 className="mb-2 font-bold text-3xl text-gray-900">
                {config.brandName}
              </h1>
              <h2 className="mb-1 font-semibold text-gray-600 text-lg uppercase tracking-wide">
                ໃບເກັບເງິນ
              </h2>
              <p className="font-medium text-gray-500 text-sm uppercase tracking-wider">
                Receipt
              </p>
            </div>

            <div className="mb-8 flex justify-between rounded-lg bg-gray-50 p-4">
              <div>
                <p className="receipt-info-label font-medium text-gray-500 text-xs uppercase">
                  ວັນທີ
                </p>
                <p className="font-semibold text-gray-900">
                  {formatDate(date)}
                </p>
              </div>
              {bookingId && (
                <div className="text-right">
                  <p className="font-medium text-gray-500 text-xs uppercase">
                    ເລກທີ
                  </p>
                  <p className="font-semibold text-gray-900">
                    {bookingId.slice(0, 8).toUpperCase()}
                  </p>
                </div>
              )}
            </div>

            <div className="mb-8">
              <p className="receipt-section-title mb-4 border-gray-200 border-b-2 pb-2 font-bold text-gray-900 text-sm uppercase tracking-wide">
                ລາຍການ
              </p>
              <div className="receipt-row flex items-start justify-between border-gray-100 border-b py-3">
                <div className="flex-1">
                  <div className="receipt-item-name font-semibold text-gray-900">
                    {title || contactName || "ຈອງຫ້ອງ"}
                  </div>
                  <div className="receipt-item-details mt-1 text-gray-600 text-sm">
                    ຫ້ອງ: {roomName}
                  </div>
                  <div className="receipt-item-details text-gray-600 text-sm">
                    ວັນທີ: {formatDate(date)}
                  </div>
                  <div className="receipt-item-details text-gray-600 text-sm">
                    ເວລາ: {formatTime(startTime)} - {formatTime(endTime)}
                  </div>
                  {assistLine ? (
                    <div className="receipt-item-details text-gray-600 text-sm">
                      {assistLine}
                    </div>
                  ) : null}
                </div>
                <div className="receipt-item-price ml-4 font-semibold text-gray-900">
                  {amount.toLocaleString("lo-LA")} ກີບ
                </div>
              </div>
            </div>

            <div className="border-red-600 border-t-3 pt-6 text-right">
              <p className="mb-2 font-semibold text-gray-600 text-sm uppercase tracking-wide">
                ຈຳນວນເງິນທີ່ຕ້ອງຊຳລະທັງໝົດ
              </p>
              <p className="font-bold text-4xl text-red-600">
                {amount.toLocaleString("lo-LA")} ກີບ
              </p>
            </div>

            <div className="mt-12 border-gray-200 border-t pt-6 text-center">
              <p className="mb-1 font-semibold text-gray-700 text-sm">
                ຂອບໃຈທີ່ໃຊ້ບໍລິການ
              </p>
              <p className="text-gray-500 text-xs">
                Thank you for your business
              </p>
            </div>
          </div>

          <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-white pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              ຍົກເລີກ
            </Button>
            <Button
              onClick={handlePrint}
              className="bg-red-600 hover:bg-red-700"
            >
              <PrinterIcon className="mr-2 size-4" />
              ພິມ
            </Button>
          </div>
        </div>
      </ScrollArea>
    </Modal>
  );
}
